import React, { useEffect, useState } from 'react';
import { IoIosArrowBack, IoMdSearch } from "react-icons/io";
import defaultPhoto from "../../assests/defaultImage.png"
import ModalWrapper from '../ModalWrapper';
import { useDispatch, useSelector } from 'react-redux';
import { getMyFriends } from '../../redux/user/userAction';
import { IoClose } from 'react-icons/io5';
import { toast } from 'react-toastify';
import SecurityVerificationModal from './SecurityVerificationModal';
import { getTransferOTP, transferCoin } from '../../redux/wallet/walletAction';
import { useQueryClient } from '@tanstack/react-query';

const UserItem = ({ user, onSelect, isSeleted }) => {
    const { firstName, lastName, profilePhoto } = user ?? {}
    const profileImage = profilePhoto && profilePhoto?.length > 0 ? profilePhoto[0].photoPublicUrl : defaultPhoto
    return (
        <div className='flex flex-col items-center justify-center py-2 px-3 rounded-lg bg-customGray gap-2'>
            <img
                src={profileImage}
                alt='user avatar'
                className='w-[50px] aspect-square rounded-full object-cover'
            />
            <p className='font-outfit text-xs text-center line-clamp-2  text-white'>{firstName} {lastName}</p>
            <button
                onClick={() => onSelect({ ...user, avatar: profileImage })}
                className={`${isSeleted ? "bg-customGold text-customDark" : "bg-customDark"} rounded-[3px] px-4 py-1`}
            >
                Select
            </button>
        </div>
    )
}

const UserSearchStep = ({ isSelected, onSelect }) => {
    const dispatch = useDispatch();
    const [query, setQuery] = useState("");
    const myFriends = useSelector((state) => state.user?.myFriends);

    const [visibleCount, setVisibleCount] = useState(12);

    useEffect(() => {
        dispatch(getMyFriends());
    }, [dispatch]);

    const filteredFriends = myFriends?.filter((user) =>
        user?.UserName?.toLowerCase().includes(query.toLowerCase()) || user?.firstName?.toLowerCase().includes(query.toLowerCase()) || user?.lastName?.toLowerCase().includes(query.toLowerCase())
    ) || [];

    const hasMore = visibleCount < filteredFriends.length;

    const loadMore = () => {
        setVisibleCount((prev) => prev + 12);
    };

    return (
        <div className='w-full space-y-3'>
            {/* Search Input */}
            <div className='w-full flex items-center gap-2 h-10 bg-customGray rounded-[5px] shadow-black shadow-sm px-2'>
                <IoMdSearch size={15} className='text-white' />
                <input
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        setVisibleCount(12);
                    }}
                    autoFocus
                    placeholder='Search'
                    className='focus:outline-none w-full h-full py-3 text-white placeholder-gray-500 placeholder:font-sora placeholder:text-xs text-xs bg-transparent'
                />
            </div>

            {filteredFriends.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-10 text-center text-gray-400 text-sm h-[60vh]">
                    <p className="font-medium">
                        {query ? "No friends found." : "You have no friends yet."}
                    </p>
                    {!query && (
                        <p className="text-xs mt-1">
                            Start connecting with people to see them here.
                        </p>
                    )}
                </div>
            ) : (
                <div className='max-h-[60vh] h-[60vh] scrollbar-hide overflow-auto'>
                    <div className='grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 h-full'>
                        {filteredFriends
                            .slice(0, visibleCount)
                            .map((user) => (
                                <UserItem
                                    key={user?.userId}
                                    user={user}
                                    isSeleted={user.userId === isSelected?.userId}
                                    onSelect={onSelect}
                                />
                            ))}
                    </div>

                    {hasMore && (
                        <div className="flex justify-center pt-2">
                            <button
                                onClick={loadMore}
                                className="text-xs px-4 py-2 bg-customGray rounded-md text-white hover:bg-gray-700 transition"
                            >
                                Load More
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default function SendMoneyModal({ onClose, onSuccess }) {
    const queryClient = useQueryClient()
    const [findFriend, setFindFriend] = useState(false)
    const [selectedUser, setSelectedUser] = useState(null)
    const [amount, setAmount] = useState(50)
    const [showVerificationModal, setShowVerificationModal] = useState(false)
    const [loading, setLoading] = useState(false)

    const handleGetSecurityOtp = async () => {
        try {
            setLoading(true)
            await getTransferOTP()
            setShowVerificationModal(true)
        }
        catch (error) {
            toast.error(error.message || "Something went wrong!")
        } finally {
            setLoading(false)
        }
    }

    const handleTransferCoin = async (code) => {
        try {
            const payload = {
                recipientId: selectedUser?.userId,
                amount: amount,
                otp: code,
                // "message": "string"
            }
            await transferCoin(payload)
            queryClient.invalidateQueries({ queryKey: ["wallet"] })
        }
        catch (error) {
            throw new Error(error.message || "Something went wrong!")
        }
    }

    if (showVerificationModal) {
        return (
            <SecurityVerificationModal
                onClose={() => setShowVerificationModal(false)}
                onSuccess={onSuccess}
                onGetCode={handleGetSecurityOtp}
                onConfirm={handleTransferCoin}
            />
        )
    }

    return (
        <ModalWrapper onClose={onClose} backgroundColor={findFriend ? "#1c1b19" : undefined} showClose={false}>
            <div className="p-6 relative">
                <div
                    onClick={findFriend ? () => setFindFriend(false) : undefined}
                    className={`flex items-center mb-2 gap-2 ${findFriend ? "cursor-pointer" : "cursor-auto"}`}
                >
                    {findFriend && <IoIosArrowBack className='text-xl text-white' />}
                    <h2 className="text-xl font-semibold">Send BD coin to friends</h2>
                </div>

                <button
                    onClick={onClose}
                    className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-full bg-white text-black hover:opacity-80 transition"
                >
                    <IoClose size={18} />
                </button>

                {!findFriend &&
                    <>
                        <div className="flex flex-col items-center mb-10 mt-10">
                            <p className="text-sm text-gray-300 mb-4">Amount</p>

                            <div className="flex items-center gap-3">
                                {/* Coin Icon */}
                                <div className="w-8 h-8 rounded-full bg-yellow-400 flex items-center justify-center font-bold text-black shadow-md">
                                    D
                                </div>

                                {/* Amount */}
                                <input
                                    type="number"
                                    value={amount}
                                    onChange={(e) => {
                                        const value = e.target.value;
                                        if (value >= 0) setAmount(value);
                                    }}
                                    className="text-6xl font-light text-gray-200 bg-transparent 
             w-32 text-center focus:outline-none 
             appearance-none [&::-webkit-inner-spin-button]:appearance-none 
             [&::-webkit-outer-spin-button]:appearance-none"
                                />
                            </div>
                        </div>

                        <div className="w-full mb-8">
                            <div className={`bg-[rgba(28,27,25,0.6)] ${selectedUser ? "p-3 border-b border-gray-500" : ""} flex items-center justify-between rounded-t-lg`}>
                                {!selectedUser &&
                                    <input
                                        onClick={() => setFindFriend(true)}
                                        onFocus={() => setFindFriend(true)}
                                        type="text"
                                        value=""
                                        placeholder="To who you want to send?"
                                        className="w-full h-full bg-transparent border-b py-4 px-4 border-gray-500 text-lg text-white placeholder-gray-400 placeholder:font-outfit focus:outline-none focus:border-white transition"
                                    />
                                }

                                {selectedUser &&
                                    <>
                                        <div className='flex items-center gap-2'>
                                            <img
                                                src={selectedUser.avatar}
                                                alt='user avatar'
                                                className='w-[50px] aspect-square rounded-full'
                                            />

                                            <p className='font-outfit text-xs'>{selectedUser.firstName} {selectedUser.lastName}</p>
                                        </div>

                                        <button
                                            onClick={() => setSelectedUser(null)}
                                            className='bg-customDark rounded-[3px] px-3 py-1'
                                        >
                                            Remove
                                        </button>
                                    </>
                                }
                            </div>
                            {!selectedUser &&
                                <p className="text-sm font-outfit text-gray-200 mt-2">
                                    Search by username or email
                                </p>
                            }
                        </div>

                        <div className="flex justify-end">
                            <button
                                onClick={handleGetSecurityOtp}
                                disabled={!selectedUser || amount === 0 || loading}
                                className="px-6 py-3 rounded-lg bg-customDark hover:shadow-lg shadow-black duration-300 transition text-white text-sm font-medium disabled:cursor-not-allowed disabled:opacity-70"
                            >
                                {loading ? "Loading..." : "Continue"}
                            </button>
                        </div>
                    </>
                }

                {findFriend &&
                    <UserSearchStep isSelected={selectedUser}
                        onSelect={(user) => {
                            setSelectedUser(user)
                            setFindFriend(false)
                        }}
                    />
                }
            </div>
        </ModalWrapper>
    );
}